import React from "react";
import {
  Stack,
  Paper,
  Typography,
  Box,
  Chip,
  LinearProgress,
  alpha,
  Tooltip,
  IconButton,
  Skeleton,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ShieldIcon from "@mui/icons-material/Shield";
import ElderlyIcon from "@mui/icons-material/Elderly";

const FLAG_ICONS = [
  { key: "highRisk", icon: <ShieldIcon sx={{ fontSize: 14 }} />, label: "High Risk", color: "#EF4444" },
  { key: "highCost", icon: <MonetizationOnIcon sx={{ fontSize: 14 }} />, label: "High Cost", color: "#F59E0B" },
  { key: "medicare", icon: <ElderlyIcon sx={{ fontSize: 14 }} />, label: "Medicare", color: "#3B82F6" },
  { key: "critical", icon: <LocalHospitalIcon sx={{ fontSize: 14 }} />, label: "Critical", color: "#8B5CF6" },
];

function PcpCard({ pcp, selected, onView, onTransfer }) {
  const usagePct = pcp.maxPatient ? Math.round((pcp.currentLoad / pcp.maxPatient) * 100) : null;
  const barColor = usagePct >= 100 ? "#EF4444" : usagePct >= 90 ? "#F59E0B" : "#10B981";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        transition: "all 0.15s",
        "&:hover": { borderColor: "primary.light" },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {pcp.title} {pcp.pcpName}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {pcp.centerName}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="View patients">
            <IconButton size="small" onClick={() => onView(pcp)}>
              <VisibilityIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Transfer all patients">
            <IconButton size="small" onClick={() => onTransfer(pcp)} disabled={pcp.currentLoad === 0}>
              <SwapHorizIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Load bar */}
      <Box sx={{ mt: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" fontWeight={600}>
            {pcp.currentLoad} patients
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {pcp.maxPatient ? `${pcp.available} available / ${pcp.maxPatient} max` : "No limit"}
          </Typography>
        </Stack>
        {usagePct !== null ? (
          <LinearProgress
            variant="determinate"
            value={Math.min(usagePct, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: (theme) => alpha(theme.palette.divider, 0.2),
              "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: barColor },
            }}
          />
        ) : (
          <LinearProgress
            variant="determinate"
            value={0}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: (theme) => alpha(theme.palette.divider, 0.2),
            }}
          />
        )}
      </Box>

      {/* Flags + Age */}
      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.5 }}>
        {FLAG_ICONS.filter((f) => pcp[f.key]).map((f) => (
          <Chip
            key={f.key}
            icon={f.icon}
            label={f.label}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: alpha(f.color, 0.1),
              color: f.color,
              "& .MuiChip-icon": { color: f.color },
            }}
          />
        ))}
        {(pcp.ageMin || pcp.ageMax) && (
          <Chip
            label={`Age: ${pcp.ageMin || "0"}–${pcp.ageMax || "∞"}`}
            size="small"
            sx={{ height: 22, fontSize: "0.65rem", fontWeight: 600, bgcolor: "#F3F4F6", color: "#374151" }}
          />
        )}
      </Stack>
    </Paper>
  );
}

export default function PcpPanel({ pcps, loading, selectedPcpId, onViewPcp, onTransferPcp }) {
  if (loading) {
    return (
      <Stack spacing={1.5}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={100} sx={{ borderRadius: 3 }} />
        ))}
      </Stack>
    );
  }

  if (!pcps || pcps.length === 0) {
    return <Typography variant="body2" color="text.secondary">No PCPs in this location.</Typography>;
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: 0.5 }}>
        PCPs ({pcps.length})
      </Typography>
      {pcps.map((pcp) => (
        <PcpCard
          key={pcp.employeePcpId}
          pcp={pcp}
          selected={pcp.employeePcpId === selectedPcpId}
          onView={onViewPcp}
          onTransfer={onTransferPcp}
        />
      ))}
    </Stack>
  );
}
